(() => {
  const namespace = 'AlhaqDesktopMenu';
  const version = 3;
  const rootSelector = '[data-alhaq-desktop-navigation]';
  const desktopQuery = window.matchMedia('(min-width: 1200px)');
  const closeDelay = 150;

  if (window[namespace]?.version === version) {
    window[namespace].refresh(document);
    return;
  }

  window[namespace]?.destroy?.();

  const controllers = new Map();
  const globalAbortController = new AbortController();
  const globalSignal = globalAbortController.signal;

  class DesktopMenuController {
    constructor(root) {
      this.root = root;
      this.navigation = root;
      this.items = [];
      this.closeTimers = new Map();
      this.usingKeyboard = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      const browseMenu = root.querySelector('.alhaq-categories > header-menu');
      const primaryMenus = Array.from(root.querySelectorAll('.alhaq-primary-menu header-menu'));

      this.items = [browseMenu, ...primaryMenus]
        .filter(Boolean)
        .map((headerMenu) => {
          const details = headerMenu.firstElementChild;
          const summary = details?.querySelector(':scope > summary');

          if (details?.tagName !== 'DETAILS' || !summary) return null;
          return { headerMenu, details, summary, pinned: false, pinOnOpen: false };
        })
        .filter(Boolean);

      this.items.forEach((item) => {
        item.details.addEventListener('pointerover', (event) => this.onPointerOver(item, event), {
          signal: this.signal,
        });
        item.details.addEventListener('pointerout', (event) => this.onPointerOut(item, event), {
          signal: this.signal,
        });
        item.details.addEventListener('focusin', () => this.cancelClose(item), { signal: this.signal });
        item.details.addEventListener('focusout', () => this.onFocusOut(item), { signal: this.signal });
        item.details.addEventListener('toggle', () => this.onToggle(item), { signal: this.signal });
        item.summary.addEventListener('click', (event) => this.onSummaryClick(item, event), {
          signal: this.signal,
        });
        this.syncExpanded(item);
      });

      this.navigation.addEventListener('pointerdown', () => (this.usingKeyboard = false), {
        signal: this.signal,
      });
      this.navigation.addEventListener('keydown', (event) => this.onNavigationKeyDown(event), {
        signal: this.signal,
      });
    }

    onPointerOver(item, event) {
      if (!desktopQuery.matches || event.pointerType === 'touch') return;
      if (event.relatedTarget && item.details.contains(event.relatedTarget)) return;

      this.usingKeyboard = false;
      this.cancelClose(item);
      if (!item.details.open) this.open(item, false);
    }

    onPointerOut(item, event) {
      if (!desktopQuery.matches || event.pointerType === 'touch') return;
      if (event.relatedTarget && item.details.contains(event.relatedTarget)) return;

      if (!item.pinned) this.scheduleClose(item);
    }

    onFocusOut(item) {
      window.setTimeout(() => {
        if (!item.details.contains(document.activeElement) && !item.pinned) this.close(item);
      });
    }

    onToggle(item) {
      if (item.details.open && item.pinOnOpen) item.pinned = true;
      item.pinOnOpen = false;
      if (!item.details.open) item.pinned = false;
      this.syncExpanded(item);
      if (desktopQuery.matches && item.details.open) this.closeSiblings(item);
    }

    onSummaryClick(item, event) {
      if (!desktopQuery.matches) return;

      this.cancelClose(item);

      if (!item.details.open) {
        item.pinOnOpen = true;
        return;
      }

      event.preventDefault();

      if (item.pinned) {
        this.close(item);
        return;
      }

      item.pinned = true;
      this.closeSiblings(item);
      this.syncExpanded(item);
    }

    onNavigationKeyDown(event) {
      if (['Tab', 'Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        this.usingKeyboard = true;
      }
    }

    scheduleClose(item) {
      this.cancelClose(item);

      const timer = window.setTimeout(() => {
        const keyboardFocusIsInside = this.usingKeyboard && item.details.contains(document.activeElement);
        if (!item.pinned && !keyboardFocusIsInside) this.close(item);
        this.closeTimers.delete(item.details);
      }, closeDelay);

      this.closeTimers.set(item.details, timer);
    }

    cancelClose(item) {
      const timer = this.closeTimers.get(item.details);
      if (timer) window.clearTimeout(timer);
      this.closeTimers.delete(item.details);
    }

    close(item) {
      this.cancelClose(item);
      item.pinned = false;
      item.pinOnOpen = false;
      item.details.removeAttribute('open');
      item.summary.setAttribute('aria-expanded', 'false');
    }

    open(item, pinned) {
      this.cancelClose(item);
      this.closeSiblings(item);
      item.pinned = pinned;
      item.pinOnOpen = false;
      item.details.setAttribute('open', '');
      this.syncExpanded(item);
    }

    closeSiblings(activeItem) {
      this.items.forEach((item) => {
        if (item !== activeItem) this.close(item);
      });
    }

    closeOutsideTarget(target) {
      this.items.forEach((item) => {
        if (item.details.open && !item.headerMenu.contains(target)) this.close(item);
      });
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }

    getOpenItemContaining(target) {
      return this.items.find((item) => item.details.open && item.headerMenu.contains(target));
    }

    hasOpenItem() {
      return this.items.some((item) => item.details.open);
    }

    syncExpanded(item) {
      item.summary.setAttribute('aria-expanded', item.details.open ? 'true' : 'false');
    }

    destroy() {
      this.closeAll();
      this.closeTimers.forEach((timer) => window.clearTimeout(timer));
      this.closeTimers.clear();
      this.abortController.abort();
      controllers.delete(this.root);
    }
  }

  const collectRoots = (scope) => {
    const roots = [];

    if (scope?.matches?.(rootSelector)) roots.push(scope);
    scope?.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));

    return roots;
  };

  const refresh = (scope = document) => {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) controller.destroy();
    });

    if (!desktopQuery.matches) return;

    collectRoots(scope).forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new DesktopMenuController(root));
    });
  };

  const destroyAll = () => {
    Array.from(controllers.values()).forEach((controller) => controller.destroy());
  };

  const onDocumentPointerDown = (event) => {
    if (!desktopQuery.matches) return;
    controllers.forEach((controller) => controller.closeOutsideTarget(event.target));
  };

  const onDocumentKeyDown = (event) => {
    if (!desktopQuery.matches || event.key !== 'Escape') return;

    let focusTarget = null;
    let hadOpenItem = false;

    controllers.forEach((controller) => {
      const focusedItem = controller.getOpenItemContaining(event.target);
      if (focusedItem) focusTarget = focusedItem.summary;
      if (controller.hasOpenItem()) hadOpenItem = true;
      controller.closeAll();
    });

    if (!hadOpenItem) return;
    event.preventDefault();
    focusTarget?.focus();
  };

  const onSectionLoad = (event) => refresh(event.target);
  const onSectionUnload = (event) => {
    controllers.forEach((controller, root) => {
      if (event.target.contains(root)) controller.destroy();
    });
  };
  const onBreakpointChange = (event) => {
    if (event.matches) {
      refresh(document);
      return;
    }

    destroyAll();
  };

  document.addEventListener('pointerdown', onDocumentPointerDown, {
    capture: true,
    signal: globalSignal,
  });
  document.addEventListener('keydown', onDocumentKeyDown, { signal: globalSignal });
  document.addEventListener('shopify:section:load', onSectionLoad, { signal: globalSignal });
  document.addEventListener('shopify:section:unload', onSectionUnload, { signal: globalSignal });
  desktopQuery.addEventListener('change', onBreakpointChange, { signal: globalSignal });

  window[namespace] = {
    version,
    refresh,
    destroy() {
      destroyAll();
      globalAbortController.abort();
      delete window[namespace];
    },
  };

  refresh(document);
})();
